import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import { type IconType, type LevelIconName, isIconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { LevelIcon } from "../LevelIcon";
import { Skeleton } from "../Skeleton";
import TooltipWrapper from "../TooltipWrapper";

export type StatTileCta = { icon: IconType; label: string };

type BaseProps = {
	label: string;
	value: string | number;
	suffix?: string;
	isLoading?: boolean;
	outlined?: boolean;
	tooltip?: string;
	cta?: StatTileCta;
	onPress?: () => void;
};

type SingleIconProps = BaseProps & {
	icon: LevelIconName;
	icons?: never;
};

type StackedIconsProps = BaseProps & {
	icon?: never;
	icons: LevelIconName[];
};

type Props = SingleIconProps | StackedIconsProps;

const TileIcon = ({
	name,
	size,
	color,
}: {
	name: LevelIconName;
	size: number;
	color: string;
}) =>
	isIconType(name) ? (
		<AppIcon name={name} size={size} color={color} />
	) : (
		<LevelIcon name={name} size={size} color={color} />
	);

const buildStackedIconEntries = (icons: LevelIconName[]) => {
	const occurrenceByName = new Map<LevelIconName, number>();

	return icons.map((name, index) => {
		const occurrence = occurrenceByName.get(name) ?? 0;
		occurrenceByName.set(name, occurrence + 1);

		return {
			name,
			key: occurrence === 0 ? name : `${name}-${occurrence}`,
			isOverlapped: index > 0,
		};
	});
};

export const StatTile = (props: Props) => {
	const {
		label,
		value,
		suffix,
		isLoading = false,
		outlined = true,
		tooltip,
		cta,
		onPress,
	} = props;
	const theme = useAppTheme();
	const accessibleValue = cta?.label ?? String(value);
	const labelText = (
		<Text variant="bodySmall" style={{ textAlign: "center" }} numberOfLines={1}>
			{label}
		</Text>
	);
	const tile = (
		<Box
			borderWidth={outlined ? 1 : 0}
			borderColor={theme.colors.surfaceVariant}
			paddingHorizontal={12}
			paddingVertical={8}
			borderRadius={theme.roundness * 4}
			width="100%"
			flexGrow={1}
			alignSelf="stretch"
			minWidth={0}
			alignItems="center"
			justifyContent="center"
			gap={4}
		>
			{isLoading ? (
				<>
					<Skeleton size={24} variant="circle" />
					<Skeleton width="80%" height={16} />
					<Skeleton width="100%" height={20} />
				</>
			) : (
				<>
					{"icons" in props && props.icons ? (
						<Box flexDirection="row" alignItems="center">
							{buildStackedIconEntries(props.icons).map(
								({ name, key, isOverlapped }) => (
									<Box
										key={key}
										backgroundColor={theme.colors.elevation.level1}
										borderWidth={2}
										borderColor={theme.colors.surface}
										borderRadius={999}
										padding={6}
										style={isOverlapped ? { marginLeft: -7 } : undefined}
									>
										<TileIcon
											name={name}
											size={16}
											color={theme.colors.primary}
										/>
									</Box>
								),
							)}
						</Box>
					) : (
						<TileIcon
							name={props.icon}
							size={24}
							color={theme.colors.primary}
						/>
					)}
					{labelText}
					{cta ? (
						<Box
							flexDirection="row"
							gap={4}
							alignItems="center"
							justifyContent="center"
						>
							<AppIcon name={cta.icon} size={16} color={theme.colors.primary} />
							<Text
								variant="titleSmall"
								numberOfLines={1}
								style={{ textAlign: "center", color: theme.colors.primary }}
							>
								{cta.label}
							</Text>
						</Box>
					) : suffix ? (
						<Box
							flexDirection="row"
							gap={4}
							alignItems="flex-end"
							justifyContent="center"
						>
							<Text
								variant="titleSmall"
								style={{ textAlign: "center" }}
								numberOfLines={1}
							>
								{value}
							</Text>
							<Box paddingBottom={1}>
								<Text
									variant="bodySmall"
									style={{ textAlign: "center" }}
									numberOfLines={1}
								>
									{suffix}
								</Text>
							</Box>
						</Box>
					) : (
						<Text
							variant="titleSmall"
							style={{ textAlign: "center" }}
							numberOfLines={1}
						>
							{value}
						</Text>
					)}
				</>
			)}
		</Box>
	);

	const pressableTile =
		onPress && !isLoading ? (
			<Pressable
				onPress={onPress}
				accessibilityRole="button"
				accessibilityLabel={`${label}, ${accessibleValue}`}
				style={{
					width: "100%",
					flexGrow: 1,
					alignSelf: "stretch",
					minWidth: 0,
				}}
			>
				{tile}
			</Pressable>
		) : (
			tile
		);

	if (!tooltip || isLoading) {
		return pressableTile;
	}

	return (
		<TooltipWrapper
			title={tooltip}
			display="flex"
			style={{ width: "100%", flexGrow: 1, alignSelf: "stretch", minWidth: 0 }}
		>
			{pressableTile}
		</TooltipWrapper>
	);
};
